import {
  ITBox,
  QboAlert,
  QboCheckbox,
  QboFormGroup,
  QboLabelGroup,
  QboModal,
  QboSelect,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import useEditSmtpSettings from './pageHooks/useEditSmtpSettings';
import useDataService from './pageHooks/useDataService';
import { SMTP_SETTINGS_DOCUMENTATION_URL } from './pageHooks/constants/editSmtpSettingsConstants';

export default function EditResellerSmtpSettingsModal(props) {
  const { onSuccess, closeModal, showModal, resellerId, resellerName, original } = props;
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState();

  const {
    mailerMethodItems,
    smtpAuthenticationItems,
    smtpSecurityItems,
    smtpTestPeriodicityItems,
  } = useDataService();

  const { fetching, form, handleSubmit, resetAll } = useEditSmtpSettings({
    resellerId,
    original,
    showModal,
    onSuccessRequest: onSuccess,
    onFailedRequest: setErrorMessage,
  });

  const closeAndReset = () => {
    resetAll();
    closeModal();
  };

  return (
    <QboModal
      open={showModal}
      onClose={closeAndReset}
      title={t('partner_list_page.edit_smtp_settings.title')}
      showCloseIcon
      preventOutsideClick>
      <ITBox direction="TransferSubscriptionFormPage__wrapper column">
        <QboFormGroup
          loadingButton={fetching}
          onSubmit={(e) => {
            setErrorMessage(null);
            handleSubmit(e);
          }}
          withGenerateAndResetButton
          buttonDisabled={fetching}
          handleOnResetButton={closeAndReset}
          secondButtonLabel="Cancel"
          firstButtonLabel="Submit">
          <QboAlert
            variant="outlined"
            type="info"
            style={{
              border: '1px solid #ebebeb',
              background: '#f3f3f3',
              fontSize: '10px !important',
              marginTop: '20px',
            }}>
            <QboTypography
              noWrap={false}
              style={{ fontSize: '13px', color: '#767676', whiteSpace: 'pre-line' }}>
              {t('partner_list_page.edit_smtp_settings.confirmation')}
            </QboTypography>
            <QboTypography
              noWrap={false}
              style={{
                fontSize: '13px',
                color: '#767676',
                whiteSpace: 'pre-line',
              }}>
              {t('partner_list_page.edit_smtp_settings.warning_documentation.message')}
              <a href={SMTP_SETTINGS_DOCUMENTATION_URL} target="blank" style={{ color: '#1c63d5' }}>
                {t('partner_list_page.edit_smtp_settings.warning_documentation.link')}
              </a>
            </QboTypography>
            {original.deliveryMethod === 'azure' && (
              <QboTypography
                noWrap={false}
                style={{
                  fontSize: '13px',
                  color: '#767676',
                  whiteSpace: 'pre-line',
                  fontWeight: 700,
                }}>
                {t('partner_list_page.edit_smtp_settings.warning_azure')}
              </QboTypography>
            )}
          </QboAlert>
          {errorMessage && (
            <QboAlert
              variant="outlined"
              type="error"
              style={{
                background: '#fdeded',
                fontSize: '10px !important',
              }}>
              <p>{t('partner_list_page.edit_smtp_settings.error')}</p>
              <p>{errorMessage}</p>
            </QboAlert>
          )}
          <ITBox
            className="TransferSubresellerPage__information-box"
            direction="column"
            sx={{ marginBottom: '20px' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <QboLabelGroup title="Partner ID" description={resellerId || '-'} />
              </Grid>
              <Grid item xs={6}>
                <QboLabelGroup title="Partner Name" description={resellerName || '-'} />
              </Grid>
            </Grid>
          </ITBox>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <QboTextField
                id="supportTicket"
                placeholder="Support Ticket"
                label="Support Ticket"
                required
                fullWidth
                value={form.values.supportTicket}
                onChange={(e) => {
                  form.change('supportTicket', e.currentTarget.value);
                }}
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.supportTicket}
              />
            </Grid>
            <Grid item xs={6}>
              <QboTextField
                id="escalationTicket"
                placeholder="Escalation Ticket"
                label="Escalation Ticket"
                fullWidth
                value={form.values.escalationTicket}
                onChange={(e) => {
                  form.change('escalationTicket', e.currentTarget.value);
                }}
                inputProps={{ maxLength: 225 }}
              />
            </Grid>
          </Grid>
          <QboTextField
            id="description"
            placeholder="Description"
            label="Description"
            fullWidth
            value={form.values.description}
            onChange={(e) => {
              form.change('description', e.currentTarget.value);
            }}
            inputProps={{ maxLength: 225 }}
          />
          <QboSelect
            label="Select Method"
            options={mailerMethodItems}
            handleChange={(e) => {
              form.change('mailerMethod', e.target.value);
            }}
            value={form.values.mailerMethod}
            defaultValue="default"
          />

          {form.values.mailerMethod === 'custom' && (
            <>
              <QboTextField
                id="fromName"
                placeholder="From Name"
                label="From Name"
                disabled={fetching}
                fullWidth
                value={form.values.fromName}
                onChange={(e) => {
                  form.change('fromName', e.currentTarget.value);
                }}
                required
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.fromName}
              />
              <QboTextField
                id="email"
                placeholder="Email"
                label="Email"
                fullWidth
                value={form.values.changeEmail ? form.values.email : original.encryptedEmail}
                disabled={fetching || !form.values.changeEmail}
                onChange={(e) => {
                  form.change('email', e.currentTarget.value);
                }}
                required={form.values.changeEmail}
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.username}
              />
              <QboCheckbox
                id="changeEmail"
                label="Change Email"
                className="ResellersPage__input-check-box"
                disabled={fetching}
                checked={form.values.changeEmail}
                onChange={(e) => {
                  form.change('changeEmail', e.currentTarget.checked);
                }}
              />
              <QboTextField
                id="username"
                placeholder="Username"
                label="Username"
                disabled={fetching}
                fullWidth
                value={form.values.username}
                onChange={(e) => {
                  form.change('username', e.currentTarget.value);
                }}
                required
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.username}
              />
              <QboTextField
                id="password"
                placeholder="Password"
                label="Password"
                type="password"
                fullWidth
                value={
                  form.values.changePassword ? form.values.password : original.encryptedPassword
                }
                disabled={fetching || !form.values.changePassword}
                onChange={(e) => {
                  form.change('password', e.currentTarget.value);
                }}
                required={form.values.changePassword}
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.username}
              />
              <QboCheckbox
                id="changePassword"
                label="Change Password"
                className="ResellersPage__input-check-box"
                disabled={fetching}
                checked={form.values.changePassword}
                onChange={(e) => {
                  form.change('changePassword', e.currentTarget.checked);
                }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <QboTextField
                    id="host"
                    placeholder="Host"
                    label="Host"
                    disabled={fetching}
                    fullWidth
                    value={form.values.host}
                    onChange={(e) => {
                      form.change('host', e.currentTarget.value);
                    }}
                    required
                    inputProps={{ maxLength: 225 }}
                    errorMessage={form.errors.host}
                  />
                </Grid>
                <Grid item xs={6}>
                  <QboTextField
                    id="port"
                    placeholder="Port"
                    label="Port"
                    disabled={fetching}
                    fullWidth
                    value={form.values.port}
                    onChange={(e) => {
                      form.change('port', e.currentTarget.value);
                    }}
                    required
                    inputProps={{ maxLength: 225 }}
                    errorMessage={form.errors.port}
                  />
                </Grid>
              </Grid>

              <QboSelect
                label="Authentication"
                options={smtpAuthenticationItems}
                disabled={fetching}
                handleChange={(e) => {
                  form.change('authentication', e.target.value);
                }}
                value={form.values.authentication}
                defaultValue=""
                errorMessage={form.errors.authentication}
              />
              <QboSelect
                label="Security"
                options={smtpSecurityItems}
                disabled={fetching}
                handleChange={(e) => {
                  form.change('security', e.target.value);
                }}
                value={form.values.security}
                defaultValue=""
                errorMessage={form.errors.security}
              />
            </>
          )}

          <QboCheckbox
            id="smtpAutoTestEnabled"
            label="Enable SMTP Auto Test"
            disabled={fetching}
            checked={form.values.smtpAutoTestEnabled}
            onChange={(e) => {
              form.change('smtpAutoTestEnabled', e.currentTarget.checked);
            }}
          />

          {form.values.smtpAutoTestEnabled && (
            <>
              <QboTextField
                id="testEmailDestination"
                placeholder="Test Email Destination"
                label="Test Email Destination"
                disabled={fetching}
                fullWidth
                value={form.values.testEmailDestination}
                onChange={(e) => {
                  form.change('testEmailDestination', e.currentTarget.value);
                }}
                required
                inputProps={{ maxLength: 225 }}
                errorMessage={form.errors.testEmailDestination}
              />
              <QboSelect
                label="Test Periodicity"
                options={smtpTestPeriodicityItems}
                disabled={fetching}
                handleChange={(e) => {
                  form.change('testPeriodicity', e.target.value);
                }}
                value={form.values.testPeriodicity}
                defaultValue=""
                errorMessage={form.errors.testPeriodicity}
              />
            </>
          )}
        </QboFormGroup>
      </ITBox>
    </QboModal>
  );
}

EditResellerSmtpSettingsModal.propTypes = {
  onSuccess: PropTypes.func,
  closeModal: PropTypes.func,
  showModal: PropTypes.bool,
  resellerId: PropTypes.number,
  resellerName: PropTypes.string,
  original: PropTypes.object,
};

EditResellerSmtpSettingsModal.defaultProps = {
  onSuccess: () => {},
  closeModal: () => {},
  showModal: false,
  resellerId: 0,
  resellerName: '',
  original: {},
};
